// src/state/utils/errorHandling.ts
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type {
  ApiErrorResponse,
  ApiStatus,
  ErrorResponse,
} from "../../types/apiResponseType";
import { actions,ActionType } from "../constants/appActions";
import {
  
  type ApiStatusType,
  getApiResponseMapping,
} from "./responseHandling";

// Extended error type to include originalStatus from RTK Query
type ExtendedFetchBaseQueryError = FetchBaseQueryError & {
  originalStatus?: number;
};

export interface ErrorHandler extends ApiStatusType {
  isNetworkError: boolean;
  isServerError: boolean;
  isAuthError: boolean;
  apiError?: ApiErrorResponse;
}

export const parseError = (
  error: ExtendedFetchBaseQueryError
): ErrorHandler => {
  // Debug logging to understand error structure
  console.log("parseError - Raw error:", error);
  console.log(
    "parseError - Error status:",
    error?.status,
    typeof error?.status
  );
  console.log("parseError - Error data:", error?.data);

  // Handle network errors first
  if (isNetworkErrorType(error)) {
    console.log("parseError - Classified as network error");
    const errorMapping = getApiResponseMapping(0, "NetworkError");
    return {
      status: 0,
      code: errorMapping.code,
      action: errorMapping.action as (typeof actions)[keyof typeof actions],
      actionType:
        errorMapping.actionType as (typeof ActionType)[keyof typeof ActionType],
      message: errorMapping.message,
      detail: errorMapping.detail,
      showDetails: errorMapping.showDetails,
      isNetworkError: true,
      isServerError: false,
      isAuthError: false,
      apiError: undefined,
    };
  }

  // Extract status and determine error types
  // Use originalStatus if available (for PARSING_ERROR cases), otherwise use status
  const status =
    (error?.originalStatus as ApiStatus) || (error?.status as ApiStatus) || 0;
  const isAuthError = status === 401 || status === 403;
  const isServerError = status >= 500;

  // Get defaults for this status code
  const errorMapping = getApiResponseMapping(status);

  // Use API error data if available, otherwise use defaults
  const apiError = error?.data as ApiErrorResponse | undefined;

  // Handle plain text error messages (like "Permission denied")
  const plainTextMessage =
    typeof error?.data === "string" && error.data.length <= 100
      ? error.data
      : undefined;

  return {
    status,
    code: errorMapping.code,
    action: errorMapping.action as (typeof actions)[keyof typeof actions],
    actionType:
      errorMapping.actionType as (typeof ActionType)[keyof typeof ActionType],
    message: plainTextMessage || apiError?.message || errorMapping.message,
    detail: apiError?.detail || errorMapping.detail,
    showDetails:
      parseErrorResponsesDetails(apiError?.errors as ErrorResponse) ||
      errorMapping.showDetails,
    isNetworkError: false,
    isServerError,
    isAuthError,
    apiError,
  };
};

const isNetworkErrorType = (error: any): boolean => {
  console.log("isNetworkErrorType - Checking error:", error);
  console.log(
    "isNetworkErrorType - Error status:",
    error?.status,
    typeof error?.status
  );
  console.log(
    "isNetworkErrorType - Original status:",
    error?.originalStatus,
    typeof error?.originalStatus
  );

  // Check if we have an originalStatus (from PARSING_ERROR, etc.) that's a valid HTTP status
  if (
    error?.originalStatus &&
    typeof error.originalStatus === "number" &&
    error.originalStatus > 0
  ) {
    console.log("isNetworkErrorType - Has originalStatus, not network error");
    return false;
  }

  // If we have a valid HTTP status code (not 0), it's not a network error
  if (error?.status && typeof error.status === "number" && error.status > 0) {
    console.log("isNetworkErrorType - Valid HTTP status, not network error");
    return false;
  }

  const networkStatuses = ["FETCH_ERROR", "PARSING_ERROR", "TIMEOUT_ERROR"];
  const networkMessages = [
    "ERR_CONNECTION_REFUSED",
    "ERR_NETWORK",
    "ERR_INTERNET_DISCONNECTED",
  ];

  const isNetworkStatus = networkStatuses.includes(error?.status);
  const isNetworkMessage = networkMessages.some((msg) =>
    error?.message?.includes(msg)
  );

  console.log("isNetworkErrorType - Network status check:", isNetworkStatus);
  console.log("isNetworkErrorType - Network message check:", isNetworkMessage);
  console.log(
    "isNetworkErrorType - Final result:",
    isNetworkStatus || isNetworkMessage
  );

  return isNetworkStatus || isNetworkMessage;
};

export const parseErrorResponsesDetails = (
  errorresponses: ErrorResponse
): string | string[] | undefined => {
  if (!errorresponses) return undefined;

  if (Array.isArray(errorresponses)) {
    if (errorresponses.length <= 0) {
      return undefined;
    }
    const errorDetails: string[] = [];

    errorresponses.forEach((errorItem: any) => {
      if (
        typeof errorItem === "object" &&
        "detail" in errorItem &&
        typeof errorItem.detail === "string" &&
        errorItem.detail.length > 0
      ) {
        errorDetails.push(errorItem.detail);
      }
    });
    return errorDetails;
  }

  if (
    typeof errorresponses === "object" &&
    "detail" in errorresponses &&
    errorresponses.detail &&
    typeof errorresponses.detail === "string" &&
    errorresponses.detail.length > 0
  ) {
    return errorresponses.detail;
  }

  return undefined;
};
